/**
 * The host's controls, on the host's own device.
 *
 * Deliberately not on the table screen. The shared screen is identical for
 * every viewer and holds no authority — the moment it grows a button, "what
 * everyone sees" and "what one person can do" stop being the same thing, and
 * the host has to walk to the television to use it.
 *
 * Every action carries the table version, so a control pressed against a stale
 * roster is refused rather than applied to whoever is in that seat now.
 */

import { useState } from 'react';
import type { MatchView } from '../net/match.js';
import type { GameLayoutWire } from '../net/session.js';
import { TurnTimer } from './TurnTimer.js';
import { isSolo } from '../net/match.js';
import { SettingRow } from './SettingRow.js';
import { Scoreboard } from './Scoreboard.js';
import { GamePicker } from './GamePicker.js';
import { Observers } from './Observers.js';

export function HostControls({
  view,
  layout: _layout,
  limits,
  onAction,
}: {
  view: MatchView;
  layout?: GameLayoutWire | undefined;
  /** Bounds the server will accept, so no offered choice can be refused. */
  limits?: { min: number; default: number; max: number } | undefined;
  onAction: (action: Record<string, unknown>) => void;
}) {
  const [confirmEnd, setConfirmEnd] = useState(false);

  if (!view.isHost) return null;

  /*
   * Opening the room is the LOBBY's job, not this panel's.
   *
   * This used to carry the first deal too, which meant the one control every
   * new host has to find was hidden behind a collapsed panel on the screen
   * they had just arrived at. What remains here is what a host reaches for
   * mid-game: pause, remove somebody, deal the next hand.
   */
  // Seats follow players, so this is a range question rather than a count.
  const seatedHere = view.seats.length;
  const legalTable = seatedHere >= view.seatRange.min && seatedHere <= view.seatRange.max;
  const canDeal = view.lifecycle === 'between' && legalTable;

  /*
   * What a host of a patience is host OF.
   *
   * Dealing again and ending the room, and nothing else: pause exists to stop
   * the clocks and there are none, the two talking switches and the watcher
   * list govern people who cannot arrive, and the turn timer is the one this
   * table deliberately never runs. See `isSolo`.
   */
  const solo = isSolo(view);

  return (
    <div className="host-panel">
          <div className="host-row">
            <button
              type="button"
              className="big-button primary"
              disabled={!canDeal}
              onClick={() => onAction({ action: 'START_ROUND' })}
            >
              Deal next round
            </button>

            {view.lifecycle === 'playing' && !solo ? (
              <button
                type="button"
                className="big-button"
                onClick={() => onAction({ action: view.paused ? 'RESUME' : 'PAUSE' })}
              >
                {view.paused ? 'Resume' : 'Pause'}
              </button>
            ) : null}
          </div>

          {/*
           * Ending the game throws away a hand everybody is in the middle of,
           * and it sits next to Pause - the control a host reaches for when
           * they want to stop for a minute. One mis-tap should not be the
           * difference, so it asks first.
           */}
          {/*
            Two controls that did the same thing, one of them talking to a
            crowd that is not there.

            A patience has one player, who is also its host - so "End game" and
            "Leave table" both mean put this deal away and go back to the
            games, and one of them asked whether everyone should lose a hand
            nobody else is in. The leave control does it, worded for one
            person; this one is for a table with other people at it.
          */}
          {view.lifecycle === 'playing' && !solo ? (
            <div className="host-row">
              {confirmEnd ? (
                <>
                  <button
                    type="button"
                    className="big-button danger"
                    onClick={() => {
                      onAction({ action: 'END_GAME' });
                      setConfirmEnd(false);
                    }}
                  >
                    End it — everyone loses this hand
                  </button>
                  <button type="button" className="text-button" onClick={() => setConfirmEnd(false)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="big-button danger-quiet"
                  onClick={() => setConfirmEnd(true)}
                >
                  End game
                </button>
              )}
            </div>
          ) : null}

          {view.hostIsTemporary ? (
            <p className="banner warn">
              You are standing in for the host. You can finish the game that is
              running; they take the room back if they return.
            </p>
          ) : null}

          {!legalTable ? (
            <p className="field-help">
              {seatedHere < view.seatRange.min
                ? `Waiting for ${String(view.seatRange.min - seatedHere)} more.`
                : `This game seats ${String(view.seatRange.max)} - move somebody to watching.`}
            </p>
          ) : null}

          <SettingRow
            icon="repeat"
            title="Keep dealing"
            help="Deal the next game automatically when this one ends"
            checked={view.autoContinue}
            onChange={(next) => onAction({ action: 'SET_AUTO_CONTINUE', enabled: next })}
          />

          {solo ? null : (
          <>
          {/*
            Here as well as in the lobby, unlike the other room settings.
            Typed messages are the one thing a host is likely to need to switch
            off DURING a game rather than before it — because the reason to
            reach for it is something that has just happened. A switch you can
            only use before anybody has spoken is not the switch anybody wants.
          */}
          <SettingRow
            icon="message"
            title="Let people type"
            help="Off leaves the quick phrases"
            checked={view.allowTextChat}
            onChange={(next) => onAction({ action: 'SET_ALLOW_TEXT_CHAT', enabled: next })}
          />

          {/*
            Mid-game, which is when a host actually wants this (L3).

            The lobby has the same switch, but the reason to reach for it
            usually arrives after the deal - somebody's room is loud, or a
            child has wandered in. Switching it off drops everyone already
            talking, because "voice off" describing a table where two people
            are still audible would be a lie.
          */}
          <SettingRow
            icon="mic"
            title="Let people talk"
            help="Off ends the conversation and stops new ones"
            checked={view.allowVoice}
            onChange={(next) => onAction({ action: 'SET_ALLOW_VOICE', enabled: next })}
          />

          <TurnTimer
            limits={limits}
            current={view.turnLimitMs}
            onChoose={(turnLimitMs) => onAction({ action: 'SET_TURN_LIMIT', turnLimitMs })}
          />

          <Observers view={view} onHostAction={onAction} />
          </>
          )}

          {/* A list of one, with nobody in it to remove: the only chair is
              yours and the button beside it is already hidden for that. */}
          {solo ? null : (
          <div className="host-seats">
            <span className="field-label">Players</span>
            {view.seats.map((seat) => (
              <div className="host-seat" key={seat.seat}>
                <span>
                  {seat.displayName}
                  {seat.away ? ' · away' : ''}
                  {!seat.connected ? ' · offline' : ''}
                </span>
                {/* Removing frees the chair so somebody else can take it over;
                    the engine's player count is fixed at the deal, so the seat
                    itself cannot disappear mid-round. */}
                {seat.seat !== view.seat ? (
                  <span className="host-seat-actions">
                    {/*
                      Stop waiting on somebody (L4, SKIP_SEAT).
                      
                      This existed on the server and had no control anywhere -
                      a host whose player had wandered off could remove them,
                      which takes their seat and their score away, or wait out
                      the clock every single turn. This is the third answer and
                      the one people actually want: the table plays on and the
                      chair is still theirs when they sit back down.

                      Not offered to a seat already away, because the server
                      simply sets the same flag again.
                    */}
                    {!seat.away ? (
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => onAction({ action: 'SKIP_SEAT', seat: seat.seat })}
                        title="Play their turns for them until they are back"
                      >
                        Skip
                      </button>
                    ) : null}

                    {/* Sends them to the watchers rather than out of the room,
                        so somebody who wants to stop playing can still watch. */}
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => onAction({ action: 'MAKE_OBSERVER', seat: seat.seat })}
                      title="Move them to watching, keeping them in the room"
                    >
                      To watching
                    </button>

                    {/* Only the original host may hand the room on, so a
                        stand-in is not offered a control that would refuse. */}
                    {!view.hostIsTemporary ? (
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => onAction({ action: 'ASSIGN_HOST', seat: seat.seat })}
                        title="Let this player finish the game if you have to leave"
                      >
                        Make host
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-button danger"
                      onClick={() => onAction({ action: 'REMOVE_SEAT', seat: seat.seat })}
                    >
                      Remove
                    </button>
                  </span>
                ) : null}
              </div>
            ))}
          </div>
          )}
    </div>
  );
}

/**
 * What the room is doing, when it is not a turn.
 *
 * A player staring at an empty screen assumes something is broken, so every
 * non-playing state says what it is waiting for and who can end the wait.
 */
export function RoomStatus({
  view,
  layout,
  onAction,
}: {
  view: MatchView;
  layout: GameLayoutWire;
  onAction?: ((action: Record<string, unknown>) => void) | undefined;
}) {
  // A lobby is a screen of its own now — code, QR, invite, settings — rather
  // than a line of status text. See `Lobby`.
  if (view.lifecycle !== 'between') return null;

  const seated = view.seats.length;
  const canDeal = seated >= view.seatRange.min && seated <= view.seatRange.max;

  return (
    <div className="room-status">
      <Scoreboard view={view} layout={layout} compact />

      {/*
       * The next hand, offered where the last one ended.
       *
       * Dealing again used to live only inside the collapsed host panel, which
       * is the wrong place for the single thing everybody is waiting on once
       * the scores are up - the room sits looking at a result while the host
       * hunts for a control. It matters more now that "keep dealing" is off by
       * default: nothing happens at all until somebody presses this.
       */}
      {view.isHost && onAction ? (
        <div className="room-status-actions">
          <button
            type="button"
            className="big-button primary"
            disabled={!canDeal}
            onClick={() => onAction({ action: 'START_ROUND' })}
          >
            {canDeal
              ? 'Play again'
              : seated < view.seatRange.min
                ? `Need ${String(view.seatRange.min - seated)} more`
                : 'Too many players'}
          </button>

          {/*
           * Playing something else, without the room ending.
           *
           * This was a button that closed the room and sent the host back to
           * their library - everybody else got the landing page and had to be
           * re-invited. The room outlives the game now, so the same people stay
           * where they are and the game changes under them.
           */}
          <GamePicker view={view} onHostAction={onAction} />
        </div>
      ) : null}

      {/* Everybody else is waiting on one person, and should be told so rather
          than left watching a screen that has stopped changing. */}
      {!view.isHost ? (
        <p className="field-help">Waiting for the host to deal again.</p>
      ) : null}
    </div>
  );
}
